import { ReactNode } from 'react';

/**
 * ChartLegend - Consistent legend component for all charts
 *
 * Pattern from previous version screenshots:
 * - Horizontal layout with colored dots
 * - Label next to each dot
 * - Wraps on mobile
 */

interface LegendItem {
  /** Legend label */
  label: string;
  /** Color (CSS color or Tailwind class) */
  color: string;
  /** Optional value to show */
  value?: string | number;
  /** Optional pattern (dashed, dotted, etc.) */
  pattern?: 'solid' | 'dashed' | 'dotted';
}

interface ChartLegendProps {
  items: LegendItem[];
  /** Layout direction */
  direction?: 'horizontal' | 'vertical';
  /** Size of legend items */
  size?: 'sm' | 'md';
  /** Alignment */
  align?: 'left' | 'center' | 'right';
  className?: string;
}

export function ChartLegend({
  items,
  direction = 'horizontal',
  size = 'sm',
  align = 'center',
  className = '',
}: ChartLegendProps) {
  const alignClass = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
  };

  const directionClass = direction === 'horizontal'
    ? `flex flex-wrap gap-x-4 gap-y-1 ${alignClass[align]}`
    : 'flex flex-col gap-1';

  const textSize = size === 'sm' ? 'text-xs' : 'text-sm';
  const dotSize = size === 'sm' ? 'w-2 h-2' : 'w-2.5 h-2.5';

  return (
    <div className={`${directionClass} ${className}`}>
      {items.map((item, index) => (
        <LegendItem
          key={index}
          item={item}
          textSize={textSize}
          dotSize={dotSize}
        />
      ))}
    </div>
  );
}

interface LegendItemInternalProps {
  item: LegendItem;
  textSize: string;
  dotSize: string;
}

function LegendItem({ item, textSize, dotSize }: LegendItemInternalProps) {
  // Check if color is a Tailwind class or CSS color
  const isTailwindClass = item.color.startsWith('bg-') || item.color.startsWith('text-');

  const dotStyle = isTailwindClass
    ? {}
    : { backgroundColor: item.color };

  const dotClass = isTailwindClass
    ? `${dotSize} rounded-full ${item.color}`
    : `${dotSize} rounded-full`;

  // For line patterns (dashed, dotted)
  if (item.pattern && item.pattern !== 'solid') {
    const lineStyle = {
      backgroundColor: isTailwindClass ? undefined : item.color,
      borderStyle: item.pattern,
      borderWidth: '1px 0 0 0',
      borderColor: isTailwindClass ? undefined : item.color,
    };

    return (
      <div className="flex items-center gap-1.5">
        <div
          className={`w-4 h-0 ${isTailwindClass ? item.color.replace('bg-', 'border-') : ''}`}
          style={lineStyle}
        />
        <span className={`${textSize} text-text-secondary`}>
          {item.label}
          {item.value !== undefined && (
            <span className="ml-1 text-white font-medium">{item.value}</span>
          )}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <div className={dotClass} style={dotStyle} />
      <span className={`${textSize} text-text-secondary`}>
        {item.label}
        {item.value !== undefined && (
          <span className="ml-1 text-white font-medium">{item.value}</span>
        )}
      </span>
    </div>
  );
}

/**
 * ChartLegendInline - Inline legend for use in chart headers
 */
interface ChartLegendInlineProps {
  items: Array<{ label: string; color: string }>;
  className?: string;
}

export function ChartLegendInline({ items, className = '' }: ChartLegendInlineProps) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {items.map((item, index) => {
        const isTailwind = item.color.startsWith('bg-');
        return (
          <span key={index} className="flex items-center gap-1 text-xs text-text-tertiary">
            <span
              className={`w-2 h-2 rounded-full ${isTailwind ? item.color : ''}`}
              style={isTailwind ? {} : { backgroundColor: item.color }}
            />
            {item.label}
          </span>
        );
      })}
    </div>
  );
}

/**
 * ElasticityLegend - Specific legend for elasticity charts
 */
interface ElasticityLegendProps {
  language?: 'en' | 'es';
  className?: string;
}

export function ElasticityLegend({ language = 'en', className = '' }: ElasticityLegendProps) {
  const t = language === 'es';

  const items: LegendItem[] = [
    { label: t ? 'Inelástico' : 'Inelastic', color: '#14b8a6' }, // teal
    { label: t ? 'Unitario' : 'Unitary', color: '#f97316' },     // orange
    { label: t ? 'Elástico' : 'Elastic', color: '#ef4444' },     // red
    { label: t ? 'Muy Elástico' : 'Very Elastic', color: '#991b1b' }, // dark red
  ];

  return <ChartLegend items={items} align="right" className={className} />;
}

/**
 * Preset color palettes for charts
 */
export const CHART_COLORS = {
  // For brand/segment shares
  categorical: ['#a855f7', '#eab308', '#22c55e', '#3b82f6', '#f97316', '#ec4899'],

  // For positive/negative
  diverging: {
    positive: '#22c55e',
    negative: '#ef4444',
    neutral: '#6b7280',
  },

  // For sequential data
  sequential: ['#0d9488', '#14b8a6', '#2dd4bf', '#5eead4', '#99f6e4'],

  // For elasticity
  elasticity: {
    inelastic: '#14b8a6',
    unitary: '#f97316',
    elastic: '#ef4444',
    veryElastic: '#991b1b',
  },

  // For status
  status: {
    approved: '#22c55e',
    review: '#f97316',
    risk: '#ef4444',
  },
};
